function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { forwardRef, useContext } from 'react';
import cn from 'clsx';
import { string, node, bool, func, object } from 'prop-types';
import { LinkAdapterContext } from '@immediate_media/component-config';
import { useCurrentUrl } from '../../hooks/index.js';
const compareUrls = (linkUrl, currentUrl) => {
  if (!currentUrl) return false;
  if (currentUrl === linkUrl) return true;
  try {
    // this can fail if linkUrl is invalid
    linkUrl = new URL(linkUrl, currentUrl).href;

    // strip trailing slashes when comparing current to link
    return linkUrl.replace(/\/$/, '') === currentUrl.replace(/\/$/, '');
  } catch (err) {
    return false;
  }
};

/**
 * Link component.
 */
const Link = /*#__PURE__*/forwardRef(({
  activeClass,
  activeParentClass,
  children,
  className,
  href,
  rel,
  media,
  target,
  title,
  aria,
  inverted,
  ellipsis,
  styled,
  onClick,
  onKeyDown,
  tabIndex,
  role,
  style,
  as,
  gtmLabel,
  ...restProps
}, ref) => {
  const LinkAdapter = useContext(LinkAdapterContext);
  const Component = as || 'a';
  const currentUrl = useCurrentUrl();
  const hasActiveClass = activeClass && href && compareUrls(href, currentUrl);
  const hasActiveParentClass = !!activeParentClass;
  return /*#__PURE__*/React.createElement(LinkAdapter, {
    href: href
  }, /*#__PURE__*/React.createElement(Component, _extends({
    className: cn('link', (hasActiveClass || hasActiveParentClass) && 'body-copy-bold', className, inverted && 'link--inverted', styled && 'link--styled', ellipsis && 'link--ellipsis', hasActiveClass && activeClass, hasActiveParentClass && activeParentClass),
    href: href,
    rel: rel,
    target: target,
    media: media,
    title: title,
    onClick: onClick,
    onKeyDown: onKeyDown,
    tabIndex: tabIndex,
    ref: ref,
    role: role,
    style: style
  }, Object.keys(aria || {}).reduce((accumalator, key) => {
    accumalator[`aria-${key}`] = aria[key];
    return accumalator;
  }, {}), {
    "data-component": "Link",
    "data-component-variant": gtmLabel,
    "data-gtm-class": gtmLabel
  }, restProps), children));
});
Link.displayName = 'Link';
Link.defaultProps = {
  activeClass: null,
  activeParentClass: null,
  children: [],
  className: null,
  as: null,
  style: null,
  href: null,
  rel: null,
  target: null,
  title: null,
  media: null,
  aria: null,
  inverted: false,
  styled: false,
  onClick: null,
  onKeyDown: null,
  tabIndex: null,
  role: null,
  gtmLabel: null,
  ellipsis: false
};
Link.propTypes = process.env.NODE_ENV !== "production" ? {
  /** Additional class that is applied if the link is active */
  activeClass: string,
  /** Additional class that is applied if the link is a parent of the active link */
  activeParentClass: string,
  /** The component's children. */
  children: node,
  /** Additional class names to add to the component root. */
  className: string,
  /** Render an alternative root tag. For example, use a button instead of an
   * anchor element.  */
  as: string,
  /** Custom inline style overrides. */
  style: object,
  /** The URL of the page the link goes to. */
  href: string,
  /** The relationship between the current document and the linked document. */
  rel: string,
  /** Where to open the linked document. */
  target: string,
  /** The title of the link, which appears as a tooltip to the user. */
  title: string,
  /** The media or device that the linked document is optimized for. */
  media: string,
  /** ARIA attributes. */
  aria: object,
  /** Use the inverted link style. */
  inverted: bool,
  /** Render with the standard link styling. */
  styled: bool,
  /** On click event handler. */
  onClick: func,
  /** On key down event handler. */
  onKeyDown: func,
  /** The HTML tab index. */
  tabIndex: string,
  /** An ARIA role. */
  role: string,
  /** A label for gtm use. */
  gtmLabel: string,
  /** Adds ellipsis when text overflows */
  ellipsis: bool
} : {};
export default Link;